<template>
  <div class="simulation-info">
    <div class="info-card">
      <h3>{{ t('simulation.info.basic.title') }}</h3>
      <div class="info-grid">
        <div class="info-item">
          <label>{{ t('simulation.info.basic.status') }}</label>
          <span :class="statusClass(simulation.status)">{{ getStatusText(simulation.status) }}</span>
        </div>
        <div class="info-item">
          <label>{{ t('simulation.info.basic.period') }}</label>
          <span>{{ t('simulation.info.periodRange', { start: displayableDate(simulation.parameters?.start_date), end: displayableDate(simulation.parameters?.end_date) }) }}</span>
        </div>
        <div class="info-item">
          <label>{{ t('simulation.info.basic.collection') }}</label>
          <span>{{ t('simulation.info.collectionSummary', { name: collectionName, count: assetCount }) }}</span>
        </div>
        <div class="info-item">
          <label>{{ t('simulation.info.basic.strategy') }}</label>
          <span>{{ strategyName }}</span>
        </div>
        <div class="info-item">
          <label>{{ t('simulation.info.basic.direction') }}</label>
          <span>{{ getDirectionText(simulation.parameters?.direction) }}</span>
        </div>
        <div class="info-item">
          <label>{{ t('simulation.info.basic.createdAt') }}</label>
          <span>{{ displayableDateTime(simulation.created_at) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatDate, formatDateTime } from '@/utils/date.js';

const props = defineProps({
  simulation: {
    type: Object,
    required: true
  }
});

const { t } = useI18n()

const simulation = computed(() => props.simulation)
const collectionName = computed(() => simulation.value.collection_name || simulation.value.collection?.name || t('common.notAvailable'))
const assetCount = computed(() => simulation.value.asset_count || simulation.value.collection?.assets?.length || 0)
const strategyName = computed(() => simulation.value.strategy_name || simulation.value.strategy?.name || t('common.notAvailable'))

const displayableDate = (dateString) => {
  if (!dateString) return t('common.notAvailable')
  try {
    return formatDate(new Date(dateString));
  } catch (e) {
    return t('common.unknown')
  }
};

const displayableDateTime = (dateString) => {
  if (!dateString) return t('common.notAvailable')
  try {
    return formatDateTime(new Date(dateString));
  } catch (e) {
    return t('common.unknown')
  }
};

const statusClass = (status) => {
  if (!status) return '';
  return `status-${status.toLowerCase()}`;
};

const getStatusText = (status) => {
  if (!status) {
    return t('common.unknown')
  }
  const normalized = String(status).toLowerCase()
  const key = `simulation.status.${normalized}`
  const translated = t(key)
  return translated === key ? status : translated
};

const getDirectionText = (direction) => {
  const normalized = direction === 'long' ? 'long' : 'short'
  const key = `simulation.info.direction.${normalized}`
  return t(key)
};
</script>

<style scoped>
.simulation-info {
  margin-bottom: 24px;
}

.info-card {
  background: white;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 20px;
}

.info-card h3 {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-item label {
  font-size: 12px;
  color: #8c8c8c;
  font-weight: 500;
}

.info-item span {
  font-size: 14px;
  color: #262626;
}

.status-completed { color: #52c41a; font-weight: 500; }
.status-running { color: #1890ff; font-weight: 500; }
.status-queued { color: #faad14; font-weight: 500; }
.status-failed { color: #ff4d4f; font-weight: 500; }
</style>
